import {
  FormField,
  TableSection,
} from '@shared/components/add-form/add-form.component';
import { TableColumn } from '@shared/components/data-table/data-table.component';

// Purchase Order Add Form Data Interface for Normal Mode
export interface PurchaseOrderAddFormData {
  purchaseRequest?: number;
  series: string;
  vendorCode: string;
  deliveryDate: string;
  description: string;
}

// Purchase Order Add Form Data Interface for Purchase Request Mode
export interface PurchaseOrderAddFormDataPR {
  purchaseRequest: number;
  series: string;
  vendorCode: string;
  deliveryDate: string;
  description: string;
}

// Purchase Order Detail Item Interface
export interface PurchaseOrderDetailItem {
  id: string;
  skuCode: string;
  skuName: string;
  spuCode: string;
  spuName: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  tax: number; // Tax percentage (0-100)
  totalValue: number;
  vendorSkuCode?: string;
  vendorSkuName?: string;
  purchaseRequestItem?: number; // For purchase request mode
  warehouseCode?: string;
  warehouseName?: string;
  receivedQuantity?: number; // Quantity accepted by the warehouse so far
}

// Unit options for dropdown
export const UNIT_OPTIONS = [
  { label: 'VND', value: 'VND' },
  { label: 'Cái', value: 'Cái' },
  { label: 'Kg', value: 'Kg' },
  { label: 'Lít', value: 'Lít' },
  { label: 'Mét', value: 'Mét' },
  { label: 'Thùng', value: 'Thùng' },
  { label: 'Hộp', value: 'Hộp' },
  { label: 'Túi', value: 'Túi' },
];

// Form fields configuration for Normal Mode
export const PURCHASE_ORDER_ADD_FORM_FIELDS: FormField[] = [
  {
    controlName: 'purchaseRequest',
    label: 'purchase_order.add.fields.purchase_request',
    type: 'searchableDropdown',
    section: 'purchase_order.add.sections.basic_information',
    required: false,
    placeholder: 'purchase_order.add.placeholders.select_purchase_request',
    options: [], // Will be populated dynamically
    showClear: true,
  },
  {
    controlName: 'series',
    label: 'purchase_order.add.fields.series',
    type: 'text',
    section: 'purchase_order.add.sections.basic_information',
    required: true,
    placeholder: 'purchase_order.add.placeholders.enter_series',
    maxLength: 255,
  },
  {
    controlName: 'vendorCode',
    label: 'purchase_order.add.fields.vendor',
    type: 'searchableDropdown',
    section: 'purchase_order.add.sections.basic_information',
    required: true,
    placeholder: 'purchase_order.add.placeholders.select_vendor',
    options: [], // Will be populated dynamically
    showClear: true,
  },
  {
    controlName: 'description',
    label: 'purchase_order.add.fields.description',
    type: 'text',
    section: 'purchase_order.add.sections.basic_information',
    required: true,
    placeholder: 'purchase_order.add.placeholders.enter_description',
    maxLength: 255,
  },
  {
    controlName: 'deliveryDate',
    label: 'purchase_order.add.fields.delivery_date',
    type: 'date',
    section: 'purchase_order.add.sections.basic_information',
    required: true,
    placeholder: 'purchase_order.add.placeholders.select_delivery_date',
    minDate: new Date(),
  },
];

// Form fields configuration for Purchase Request Mode
export const PURCHASE_ORDER_ADD_FORM_FIELDS_PR: FormField[] = [
  {
    controlName: 'purchaseRequest',
    label: 'purchase_order.add.fields.purchase_request',
    type: 'text',
    section: 'purchase_order.add.sections.basic_information',
    required: true,
    placeholder: 'purchase_order.add.placeholders.enter_purchase_request',
    disabled: true, // Auto-filled from URL parameter
    readonly: true, // Make it read-only to show formatted text
    defaultValue: '', // Will be set dynamically with formatted string
  },
  {
    controlName: 'series',
    label: 'purchase_order.add.fields.series',
    type: 'text',
    section: 'purchase_order.add.sections.basic_information',
    required: true,
    placeholder: 'purchase_order.add.placeholders.enter_series',
    maxLength: 255,
  },
  {
    controlName: 'vendorCode',
    label: 'purchase_order.add.fields.vendor',
    type: 'searchableDropdown',
    section: 'purchase_order.add.sections.basic_information',
    required: false,
    placeholder: 'purchase_order.add.placeholders.select_vendor',
    options: [], // Will be populated dynamically
    showClear: true,
  },
  {
    controlName: 'description',
    label: 'purchase_order.add.fields.description',
    type: 'text',
    section: 'purchase_order.add.sections.basic_information',
    required: true,
    placeholder: 'purchase_order.add.placeholders.enter_description',
    maxLength: 255,
  },
  {
    controlName: 'deliveryDate',
    label: 'purchase_order.add.fields.delivery_date',
    type: 'date',
    section: 'purchase_order.add.sections.basic_information',
    required: true,
    placeholder: 'purchase_order.add.placeholders.select_delivery_date',
    minDate: new Date(),
  },
];

/** Shared column config for PO detail table (Add / Edit / View) */
export const PURCHASE_ORDER_DETAIL_TABLE_COLUMNS: TableColumn[] = [
  {
    label: 'purchase_order.add.table.sku_code',
    prop: 'skuCode',
    width: 120,
    align: 'left',
  },
  {
    label: 'purchase_order.add.table.sku_name',
    prop: 'skuName',
    width: 200,
    align: 'left',
  },
  {
    label: 'purchase_order.add.table.spu_code',
    prop: 'spuCode',
    width: 120,
    align: 'left',
  },
  {
    label: 'purchase_order.add.table.spu_name',
    prop: 'spuName',
    width: 200,
    align: 'left',
  },
  {
    label: 'purchase_order.add.table.vendor_sku_code',
    prop: 'vendorSkuCode',
    width: 140,
    align: 'left',
  },
  {
    label: 'purchase_order.add.table.vendor_sku_name',
    prop: 'vendorSkuName',
    width: 140,
    align: 'left',
  },
  {
    label: 'purchase_order.add.table.warehouse',
    prop: 'warehouseCode',
    width: 200,
    align: 'left',
  },
  {
    label: 'purchase_order.add.table.quantity',
    prop: 'quantity',
    width: 140,
    align: 'left',
    type: 'inputNumber',
    useGrouping: true,
  },
  {
    label: 'purchase_order.add.table.unit_price',
    prop: 'unitPrice',
    width: 120,
    align: 'right',
    type: 'currency',
  },
  {
    label: 'purchase_order.add.table.tax',
    prop: 'tax',
    width: 100,
    align: 'right',
    type: 'inputNumber',
    min: 0,
    max: 100,
  },
  {
    label: 'purchase_order.add.table.total_value',
    prop: 'totalValue',
    width: 100,
    align: 'right',
    type: 'currency',
  },
  {
    label: 'common.table.columns.actions',
    prop: 'actions',
    width: 80,
    align: 'center',
    type: 'actions',
  },
];

export const PURCHASE_ORDER_DETAIL_TABLE_SECTION: TableSection = {
  title: 'purchase_order.add.sections.detail_items',
  show: true,
  data: [],
  columns: PURCHASE_ORDER_DETAIL_TABLE_COLUMNS.filter(
    (c) => c.prop !== 'actions',
  ),
  hasCheckbox: false,
};

// Detail items table section for normal mode (with actions and editable quantity)
export const PURCHASE_ORDER_DETAIL_TABLE_SECTION_NORMAL: TableSection = {
  title: 'purchase_order.add.sections.detail_items',
  show: true,
  data: [],
  columns: [...PURCHASE_ORDER_DETAIL_TABLE_COLUMNS],
  actions: [
    {
      label: 'common.actions.delete',
      action: 'deleteSelectedDetail',
      severity: 'danger' as const,
      outlined: true,
      disabled: true,
      icon: 'delete',
    },
    {
      label: 'common.actions.new',
      action: 'addDetail',
      severity: 'info' as const,
      icon: 'add',
    },
  ],
  hasCheckbox: true,
};

// Detail items table section for view (without actions column)
export const PURCHASE_ORDER_DETAIL_TABLE_SECTION_VIEW: TableSection = {
  title: 'purchase_order.add.sections.detail_items',
  show: true,
  data: [],
  columns: PURCHASE_ORDER_DETAIL_TABLE_COLUMNS.filter(
    (c) => c.prop !== 'actions',
  ),
  actions: [],
  hasCheckbox: false,
};

// Header actions
export const PURCHASE_ORDER_HEADER_ACTIONS = [
  {
    label: 'common.actions.save',
    action: 'save',
    severity: 'info' as const,
  },
  {
    label: 'common.actions.cancel',
    action: 'cancel',
    outlined: true,
    severity: 'secondary' as const,
  },
];
